import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

// Distancia em metros entre dois pontos do mapa.
export function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Desiste depois de um tempo, aconteca o que acontecer. No iPhone, qualquer pedido
// de localizacao ou de permissao pode ficar parado para sempre; por isso TODO pedido
// passa por aqui, e a tela nunca fica esperando sem fim.
function withTimeout(promise, ms, detail) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject({ code: 3, detail: detail || "timed out" }), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });
}

// Transforma qualquer erro de localizacao em { code, detail }:
// code 1 = permissao negada, 2 = indisponivel, 3 = demorou demais.
// detail = o texto original do erro (aparece na tela para a gente entender o problema).
function normalizeGeoError(e) {
  const detail = String((e && (e.detail || e.message || e.errorMessage || e.code)) || e || "unknown");
  if (e && (e.code === 1 || e.code === 2 || e.code === 3)) return { code: e.code, detail };
  const msg = detail.toLowerCase();
  if (msg.includes("denied") || msg.includes("permission")) return { code: 1, detail };
  if (msg.includes("timeout") || msg.includes("timed out") || msg.includes("did not")) return { code: 3, detail };
  return { code: 2, detail };
}

function toPosition(p) {
  return { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy };
}

/* ---------- navegador ---------- */

function webRead(options) {
  return withTimeout(
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject({ code: 2, detail: "geolocation not supported" });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (p) => resolve(toPosition(p)),
        (e) => reject({ code: e.code, detail: e.message || `error ${e.code}` }),
        options
      );
    }),
    options.timeout + 1500,
    "browser location did not answer"
  );
}

async function webPosition(timeoutMs) {
  try {
    return await webRead({ enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 10000 });
  } catch (e) {
    const err = normalizeGeoError(e);
    if (err.code === 1) throw err;
    // Segunda tentativa: precisao normal (Wi-Fi/antenas), aceita uma posicao de ate 5 min atras.
    return webRead({ enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 });
  }
}

/* ---------- app do iPhone (plugin do Capacitor) ---------- */

async function nativeRead(options, ms) {
  const p = await withTimeout(Geolocation.getCurrentPosition(options), ms + 2000, "GPS did not answer");
  return toPosition(p);
}

async function nativePosition(timeoutMs) {
  let perm = await withTimeout(
    Geolocation.checkPermissions(),
    5000,
    "plugin did not respond (checkPermissions)"
  );
  if (perm.location !== "granted") {
    perm = await withTimeout(
      Geolocation.requestPermissions(),
      15000,
      "the location permission prompt did not finish"
    );
  }
  if (perm.location !== "granted") throw { code: 1, detail: `permission ${perm.location}` };

  try {
    return await nativeRead({ enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 10000 }, timeoutMs);
  } catch (e) {
    const err = normalizeGeoError(e);
    if (err.code === 1) throw err;
    // Segunda tentativa: precisao normal, aceita uma posicao de ate 5 min atras.
    return nativeRead({ enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }, 5000);
  }
}

async function getPositionInner(timeoutMs) {
  if (Capacitor.isNativePlatform()) {
    try {
      return await nativePosition(timeoutMs);
    } catch (e) {
      const err = normalizeGeoError(e);
      // O recurso nativo nao esta instalado ou nao responde: tenta o do navegador.
      if (/unimplemented|not implemented|not available|plugin did not respond/i.test(err.detail)) {
        try {
          return await webPosition(timeoutMs);
        } catch (e2) {
          const err2 = normalizeGeoError(e2);
          throw { code: err2.code, detail: `${err.detail} | browser: ${err2.detail}` };
        }
      }
      throw err;
    }
  }
  try {
    return await webPosition(timeoutMs);
  } catch (e) {
    throw normalizeGeoError(e);
  }
}

// Pega a localizacao. Sempre termina em, no maximo, 30 segundos: ou devolve a posicao,
// ou um erro { code, detail } que a tela mostra.
export async function getPosition(timeoutMs = 8000) {
  try {
    return await withTimeout(getPositionInner(timeoutMs), 30000, "location took too long");
  } catch (e) {
    throw normalizeGeoError(e);
  }
}

export function geoErrorMessage(e) {
  if (e && e.code === 1) {
    return "Location is turned off for SiteSync. Turn it on in Settings > Privacy and Security > Location Services > SiteSync, then try again.";
  }
  if (e && e.code === 3) {
    return "Could not get your location in time. Check that Location Services is on for SiteSync (Settings > Privacy and Security > Location Services), or move to an open area, and try again.";
  }
  return "Your location is not available right now. Check that Location Services is on and try again.";
}
